import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Check, Calendar, Gauge, Fuel, Settings2, Palette, Zap, Cog } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api, euro, km } from "../lib/api";
import MotoGallery from "../components/MotoGallery";

export default function MotorDetail() {
  const { id } = useParams();
  const [moto, setMoto] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/motorcycles/${id}`).then((r) => setMoto(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useSEO({
    title: moto ? `${moto.merk} ${moto.model} (${moto.bouwjaar || ""}) — TT Motorsports` : "Motor — TT Motorsports",
    description: moto ? `${moto.merk} ${moto.model}, ${moto.bouwjaar}, ${km(moto.kilometerstand, moto.meeteenheid)}. ${moto.beschrijving?.slice(0, 120)}` : "",
  });

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="moto-notfound">
        <p className="heading-xl text-4xl text-white mb-4">Motor niet gevonden</p>
        <Link to="/motoren" className="text-[#E10600] font-display uppercase">← Terug naar aanbod</Link>
      </div>
    );
  }
  if (!moto) return <div className="pt-40 pb-40 text-center text-[#9A9CA3] font-mono-tt">Laden...</div>;

  const images = moto.images?.length ? moto.images : ["https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=1200"];
  const specs = [
    { icon: Calendar, label: "Bouwjaar", value: moto.bouwjaar || "—" },
    { icon: Gauge, label: moto.meeteenheid === "uren" ? "Urenstand" : "Kilometerstand", value: km(moto.kilometerstand, moto.meeteenheid) },
    { icon: Cog, label: "Cilinderinhoud", value: moto.cilinderinhoud || "—" },
    { icon: Zap, label: "Vermogen", value: moto.vermogen || "—" },
    { icon: Fuel, label: "Brandstof", value: moto.brandstof || "—" },
    { icon: Settings2, label: "Transmissie", value: moto.transmissie || "—" },
    { icon: Palette, label: "Kleur", value: moto.kleur || "—" },
  ];

  return (
    <div data-testid="moto-detail-page" className="pt-28 pb-24 bg-[#0A0A0B]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <Link to="/motoren" data-testid="back-to-list" className="inline-flex items-center gap-2 text-[#9A9CA3] hover:text-[#E10600] font-display uppercase text-sm font-600 mb-8 transition-colors">
          <ArrowLeft size={16} /> Terug naar aanbod
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <MotoGallery images={images} alt={`${moto.merk} ${moto.model}`} />
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className={`font-display uppercase text-xs font-700 tracking-wider px-3 py-1.5 ${moto.status === "verkocht" ? "bg-[#2A2B31] text-[#9A9CA3]" : "bg-[#E10600] text-white"}`}>
                {moto.status === "verkocht" ? "Verkocht" : "Beschikbaar"}
              </span>
              <span className="font-mono-tt text-xs text-[#E10600] uppercase tracking-widest">{moto.merk}</span>
            </div>
            <h1 className="heading-xl text-4xl md:text-6xl text-white mb-4">{moto.model}</h1>
            <p className="font-display font-800 text-4xl text-white mb-8">{euro(moto.prijs)}</p>

            <div className="grid grid-cols-2 gap-px bg-[#2A2B31] border border-[#2A2B31] mb-8">
              {specs.map((s) => (
                <div key={s.label} className="bg-[#141518] p-4">
                  <div className="flex items-center gap-2 text-[#9A9CA3] mb-1">
                    <s.icon size={15} className="text-[#E10600]" />
                    <span className="font-mono-tt text-[11px] uppercase tracking-wider">{s.label}</span>
                  </div>
                  <p className="font-display font-600 text-white text-lg">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="detail-cta-contact" className="flex-1 min-w-[200px] bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-6 py-4 text-center transition-colors">
                Interesse? Contacteer ons
              </Link>
              <a href="tel:0485741524" className="border border-[#2A2B31] hover:border-white text-white px-6 py-4 flex items-center gap-2 font-display uppercase font-700 transition-colors">
                <Phone size={18} /> Bel ons
              </a>
            </div>
          </div>
        </div>

        {/* Description & details */}
        <div className="grid md:grid-cols-3 gap-10 mt-16 pt-12 border-t border-[#2A2B31]">
          <div className="md:col-span-2">
            {moto.beschrijving && (
              <>
                <h2 className="heading-xl text-2xl text-white mb-4">Beschrijving</h2>
                <p className="text-[#B8B9BE] leading-relaxed whitespace-pre-line mb-10">{moto.beschrijving}</p>
              </>
            )}
            {moto.onderhoudshistoriek && (
              <>
                <h2 className="heading-xl text-2xl text-white mb-4">Onderhoudshistoriek</h2>
                <p className="text-[#B8B9BE] leading-relaxed whitespace-pre-line">{moto.onderhoudshistoriek}</p>
              </>
            )}
          </div>
          {moto.opties?.length > 0 && (
            <div>
              <h2 className="heading-xl text-2xl text-white mb-4">Opties & extra's</h2>
              <ul className="space-y-2.5">
                {moto.opties.map((o, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-[#E6E6E8]">
                    <Check size={16} className="text-[#E10600] shrink-0" /> {o}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
